import React, { useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { isAuthenticated, clearAuth } from './services/api';
import Login from './pages/Login';
import Layout from './components/Layout';
import FleetOverview from './pages/FleetOverview';
import ProjectList from './pages/ProjectList';
import ProjectDetail from './pages/ProjectDetail';
import RobotJournal from './pages/RobotJournal';
import TechnicianKPI from './pages/TechnicianKPI';
import ErrorStats from './pages/ErrorStats';
import SpareParts from './pages/SpareParts';
import FaultAssignment from './pages/FaultAssignment';
import Logistics from './pages/Logistics';
import ABTesting from './pages/ABTesting';
import Regions from './pages/Regions';
import Training from './pages/Training';
import TechnicianManagement from './pages/TechnicianManagement';
import Analytics from './pages/Analytics';
import RobotSummary from './pages/RobotSummary';
import FieldFeedback from './pages/FieldFeedback';
import ErrorsTrend from './pages/ErrorsTrend';
import AnalyticalReports from './pages/AnalyticalReports';
// v1.10.8 (2026-05-09) — User Management + Inventory Dashboard
import Users from './pages/Users';
import Inventory from './pages/Inventory';

// Technician Mobile App
import TechApp from './pages/tech/TechApp';

function ProtectedRoute({ children }) {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

export default function App() {
  const [authState, setAuthState] = useState(isAuthenticated());

  const handleLogin = useCallback(() => {
    setAuthState(true);
  }, []);

  const handleLogout = useCallback(() => {
    clearAuth();
    setAuthState(false);
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        {/* Login page — always accessible */}
        <Route path="/login" element={
          authState ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
        } />

        {/* Technician mobile app — protected */}
        <Route path="/tech/*" element={
          <ProtectedRoute><TechApp /></ProtectedRoute>
        } />

        {/* Manager dashboard — protected, with sidebar layout */}
        <Route path="*" element={
          <ProtectedRoute>
            <Layout onLogout={handleLogout}>
              <Routes>
                <Route path="/" element={<FleetOverview />} />
                <Route path="/projects" element={<ProjectList />} />
                <Route path="/projects/:projectId" element={<ProjectDetail />} />
                <Route path="/robot/:projectId/:robotNumber" element={<RobotJournal />} />
                <Route path="/technicians" element={<TechnicianKPI />} />
                <Route path="/errors" element={<ErrorStats />} />
                <Route path="/parts" element={<SpareParts />} />
                <Route path="/assignments" element={<FaultAssignment />} />
                <Route path="/logistics" element={<Logistics />} />
                <Route path="/ab-testing" element={<ABTesting />} />
                <Route path="/regions" element={<Regions />} />
                <Route path="/training" element={<Training />} />
                <Route path="/technician-mgmt" element={<TechnicianManagement />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/robot-summary" element={<RobotSummary />} />
                <Route path="/field-feedback" element={<FieldFeedback />} />
                <Route path="/errors-trend" element={<ErrorsTrend />} />
                <Route path="/analytical-reports" element={<AnalyticalReports />} />
                {/* v1.10.8 — User Management + Inventory Dashboard */}
                <Route path="/users" element={<Users />} />
                <Route path="/inventory" element={<Inventory />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        } />
      </Routes>
    </BrowserRouter>
  );
}
